import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Target,
  TrendingUp,
  AlertCircle,
  Plus,
  X,
  Loader2,
  HelpCircle,
  ListChecks,
  CheckSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import Button from '../../components/ui/Button';
import Card, { CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import Tooltip from '../../components/ui/Tooltip';
import { caseAnalysisAPI } from '../../lib/api';
import { useAppStore } from '../../stores/appStore';
import type {
  SuggestDomainsResponse,
  DomainSuggestion,
  RecommendedDomain,
  ExistingHypothesis,
  AIGeneratedHypothesis,
  CoverageStatus,
  RecommendationSignal,
  KGAreaType
} from '../../types/caseAnalysis';

interface Step0Props {
  caseId: string;
  wizardData: any;
  onNext: () => void;
  onBack: () => void;
}

/**
 * Display names for the backend KG-area taxonomy.
 *
 * Typed as a total Record<KGAreaType, string>, so if the taxonomy gains or
 * renames an area this fails to compile rather than silently rendering a raw
 * id — the failure mode that let "inventory_management" and "metrics_kpis"
 * survive against a backend that never emitted them.
 */
const KG_AREA_LABELS: Record<KGAreaType, string> = {
  customer_hub: 'Customer Hub',
  supplier_hub: 'Supplier Hub',
  product_hub: 'Product Hub',
  employee_hub: 'Employee Hub',
  location_hub: 'Location Hub',
  order_to_cash: 'Order-to-Cash',
  procure_to_pay: 'Procure-to-Pay',
  inventory_mgmt: 'Inventory Mgmt',
  plan_to_produce: 'Plan-to-Produce',
  finance_cost: 'Finance & Cost',
  metrics: 'Metrics & KPIs',
};

/** Label for a KG area id, falling back to the raw id if the backend adds one. */
const formatKGArea = (area: string): string =>
  KG_AREA_LABELS[area as KGAreaType] ?? area;

export const Step0DomainSelection: React.FC<Step0Props> = ({
  caseId,
  wizardData,
  onNext,
  onBack
}) => {
  const currentUser = useAppStore((state) => state.currentUser);
  const [selectedHypotheses, setSelectedHypotheses] = useState<Set<string>>(new Set());
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  const [showCustomForm, setShowCustomForm] = useState(false);

  // Fetch domain suggestions (takes 30-60s)
  const {
    data: suggestions,
    isLoading: loadingSuggestions,
    error: suggestionsError
  } = useQuery<SuggestDomainsResponse>({
    queryKey: ['domain-suggestions', caseId],
    queryFn: () => caseAnalysisAPI.suggestDomains(caseId, false, true, 0.7),
    retry: 1,
    staleTime: 5 * 60 * 1000  // Cache for 5 minutes
  });

  // Pre-select hypotheses that were auto-selected by AI
  useEffect(() => {
    if (suggestions && selectedHypotheses.size === 0) {
      const preSelected = new Set<string>();

      suggestions.suggestions.forEach((domainSuggestion) => {
        domainSuggestion.recommended_domains.forEach((domain) => {
          // Add pre-selected existing hypotheses
          domain.existing_hypotheses
            .filter((h) => h.pre_selected)
            .forEach((h) => preSelected.add(h.hypothesis_id));

          // Add pre-selected AI hypotheses
          domain.ai_generated_hypotheses
            .filter((h) => h.pre_selected)
            .forEach((h) => preSelected.add(h.temp_id));
        });
      });

      setSelectedHypotheses(preSelected);
    }
  }, [suggestions]);

  // Approve selection mutation
  const approveMutation = useMutation({
    mutationFn: async () => {
      const selectedExisting: string[] = [];
      const selectedAi: string[] = [];

      suggestions?.suggestions.forEach((domainSuggestion) => {
        domainSuggestion.recommended_domains.forEach((domain) => {
          domain.existing_hypotheses.forEach((h) => {
            if (selectedHypotheses.has(h.hypothesis_id)) {
              selectedExisting.push(h.hypothesis_id);
            }
          });
          domain.ai_generated_hypotheses.forEach((h) => {
            if (selectedHypotheses.has(h.temp_id)) {
              selectedAi.push(h.temp_id);
            }
          });
        });
      });

      return caseAnalysisAPI.approveDomainSelection(
        caseId,
        selectedExisting,
        selectedAi,
        [],  // Custom hypotheses (future enhancement)
        currentUser?.email || 'unknown',
        `Selected ${selectedExisting.length + selectedAi.length} hypotheses for analysis`
      );
    },
    onSuccess: () => {
      toast.success('Domain selection approved!');
      onNext();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to approve selection');
    }
  });

  const toggleHypothesis = (id: string) => {
    const newSet = new Set(selectedHypotheses);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedHypotheses(newSet);
  };

  const toggleDomain = (domainKey: string) => {
    const newSet = new Set(expandedDomains);
    if (newSet.has(domainKey)) {
      newSet.delete(domainKey);
    } else {
      newSet.add(domainKey);
    }
    setExpandedDomains(newSet);
  };

  // The recommended hypothesis per question, taken from the BACKEND ranker.
  //
  // This replaces a client-side scorer that ranked hypotheses by token overlap
  // between the question text and the hypothesis prose. Measured against the
  // backend on real Dabur data the two disagreed on 11 of 12 questions, and the
  // client scorer was the weaker of the pair on two counts:
  //
  //   - Its primary signal (token overlap) predicts nothing downstream. The
  //     backend ranks on KG-area overlap, which is the same signal the
  //     schema-coverage calculation uses at Step 5, so a backend
  //     recommendation predicts what will actually score there.
  //   - Its scores were compressed into 0.43-0.50 (vs 0.335-0.955) because
  //     trackRecord carried 15% of the weight and is always 0 for AI-generated
  //     hypotheses — and every hypothesis here is AI-generated.
  //
  // Selection is by `pre_selected`, which the backend sets on the winner; the
  // tooltip is driven by `recommendation_signals`, whose contributions sum to
  // `recommendation_score`.
  const recommendedHypothesisByQuestion = useMemo<
    Map<number, { id: string; score?: number; signals: RecommendationSignal[] }>
  >(() => {
    const recommended = new Map<
      number,
      { id: string; score?: number; signals: RecommendationSignal[] }
    >();

    (suggestions?.suggestions ?? []).forEach((domainSuggestion: any, qIdx: number) => {
      let best: { id: string; score?: number; signals: RecommendationSignal[] } | null = null;

      (domainSuggestion.recommended_domains ?? []).forEach((domain: any) => {
        [
          ...(domain.existing_hypotheses ?? []),
          ...(domain.ai_generated_hypotheses ?? []),
        ].forEach((h: any) => {
          if (!h?.pre_selected) return;
          const id = h.hypothesis_id || h.temp_id;
          if (!id) return;

          // A question can have pre_selected hypotheses in more than one
          // domain; keep the highest-scoring so exactly one badge renders.
          const score = typeof h.recommendation_score === 'number' ? h.recommendation_score : undefined;
          if (!best || (score ?? -1) > (best.score ?? -1)) {
            best = {
              id,
              score,
              signals: Array.isArray(h.recommendation_signals) ? h.recommendation_signals : [],
            };
          }
        });
      });

      if (best) recommended.set(qIdx, best);
    });

    return recommended;
  }, [suggestions]);


  const getCoverageColor = (status: CoverageStatus) => {
    switch (status) {
      case 'excellent':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'good':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'moderate':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'limited':
        return 'text-red-600 bg-red-50 border-red-200';
    }
  };

  const getRecommendationColor = (rec: string) => {
    switch (rec) {
      case 'PROCEED':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'PROCEED_WITH_ENRICHMENT':
        return 'text-amber-700 bg-amber-50 border-amber-200';
      case 'CREATE_NEW':
        return 'text-red-700 bg-red-50 border-red-200';
      default:
        return 'text-neutral-600 bg-neutral-50 border-neutral-200';
    }
  };

  // Coverage thresholds map to recommendation tiers (≥80 proceed, ≥60 enrichment, else create new).
  const getCoverageTextColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-amber-600';
    return 'text-red-600';
  };

  const getCoverageBarColor = (score: number) => {
    if (score >= 0.8) return 'bg-green-500';
    if (score >= 0.6) return 'bg-amber-500';
    return 'bg-red-500';
  };

  // Backend returns SCREAMING_SNAKE_CASE recommendation enums.  Convert to
  // human-readable Title Case ("PROCEED_WITH_ENRICHMENT" → "Proceed with Enrichment").
  // The previous code used `.replace('_', ' ')` which only replaced the FIRST
  // underscore, leaving "PROCEED WITH_ENRICHMENT".
  const formatRecommendation = (rec: string): string => {
    const stopWords = new Set(['with', 'and', 'or', 'a', 'an', 'the', 'of', 'in']);
    return rec
      .split('_')
      .map((word, i) => {
        const lower = word.toLowerCase();
        if (i > 0 && stopWords.has(lower)) return lower;
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  };

  // Loading state (30-60s for AI processing)
  if (loadingSuggestions) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="relative mb-6">
          <Loader2 className="w-16 h-16 text-primary-600 animate-spin" />
          <Brain className="w-8 h-8 text-primary-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
        </div>
        <h3 className="text-xl font-semibold text-neutral-900 mb-2">
          Analyzing Strategic Questions
        </h3>
        <p className="text-neutral-600 text-center max-w-md mb-4">
          Our AI is analyzing your strategic questions, discovering relevant domain packs,
          and generating hypothesis recommendations. This typically takes 30-60 seconds.
        </p>
        <div className="flex flex-col gap-2 text-sm text-neutral-500 mt-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span>Phase 1: Question Analysis</span>
          </div>
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Phase 2: Domain Pack Discovery...</span>
          </div>
          <div className="flex items-center gap-2 opacity-50">
            <span className="w-4 h-4" />
            <span>Phase 3: Coverage Assessment</span>
          </div>
          <div className="flex items-center gap-2 opacity-50">
            <span className="w-4 h-4" />
            <span>Phase 4: AI Hypothesis Generation</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (suggestionsError) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertCircle className="w-16 h-16 text-red-600 mb-4" />
        <h3 className="text-xl font-semibold text-neutral-900 mb-2">
          Failed to Load Suggestions
        </h3>
        <p className="text-neutral-600 text-center max-w-md mb-6">
          {(suggestionsError as Error).message || 'An error occurred'}
        </p>
        <Button onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!suggestions) {
    return null;
  }

  const selectedCount = selectedHypotheses.size;
  const totalCount = suggestions.total_hypotheses_count;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Target className="w-8 h-8 text-primary-600" />
          <h2 className="text-2xl font-bold text-neutral-900">
            Domain & Hypothesis Selection
          </h2>
        </div>
        <p className="text-neutral-600 max-w-3xl mx-auto">
          Review AI-recommended domains and hypotheses for your strategic questions.
          We have pre-selected high-confidence hypotheses, but you can adjust the selection.
        </p>
      </div>

      {/* Overall Summary Card */}
      <Card className="border-2 border-primary-200 bg-gradient-to-br from-primary-50/40 to-white">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
            {/* Coverage with progress bar */}
            <div className="flex flex-col gap-2 md:border-r md:border-neutral-200 md:pr-6">
              <div className="flex items-center gap-2 text-neutral-700">
                <TrendingUp className="w-4 h-4 text-primary-600" />
                <p className="text-sm font-medium">
                  {suggestions.coverage_basis === 'ai_only' ? 'AI Analysis' : 'Overall Coverage'}
                </p>
              </div>
              {/*
                With no curated domain packs installed, overall_coverage_score is
                arithmetically constant — (0 + 0.7 x n) / n = 0.70 for every
                question, case and industry. A number that cannot vary is not a
                measurement, so show the actual counts instead. The percentage
                branch stays intact for when coverage_basis becomes
                'domain_packs' and the real measurement returns.
              */}
              {suggestions.coverage_basis === 'ai_only' ? (
                <>
                  <p className="text-3xl font-bold text-orange-600">
                    {suggestions.ai_generated_count}
                  </p>
                  <p className="text-xs text-neutral-600 mt-1">
                    hypotheses generated across{' '}
                    {suggestions.suggestions.reduce(
                      (n, s) => n + s.recommended_domains.length,
                      0,
                    )}{' '}
                    domains
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-baseline gap-2">
                    <p className={`text-4xl font-bold ${getCoverageTextColor(suggestions.overall_coverage_score)}`}>
                      {Math.round(suggestions.overall_coverage_score * 100)}%
                    </p>
                  </div>
                  <div className="w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${getCoverageBarColor(suggestions.overall_coverage_score)}`}
                      style={{ width: `${Math.round(suggestions.overall_coverage_score * 100)}%` }}
                    />
                  </div>
                </>
              )}
              <Badge
                className={`${getRecommendationColor(suggestions.recommendation)} border w-fit text-xs font-medium`}
              >
                {formatRecommendation(suggestions.recommendation)}
              </Badge>
            </div>

            {/* Strategic Questions */}
            <div className="flex flex-col gap-2 md:border-r md:border-neutral-200 md:pr-6">
              <div className="flex items-center gap-2 text-neutral-700">
                <HelpCircle className="w-4 h-4 text-blue-600" />
                <p className="text-sm font-medium">Strategic Questions</p>
              </div>
              <p className="text-4xl font-bold text-neutral-900">
                {suggestions.suggestions.length}
              </p>
              <p className="text-xs text-neutral-500">analyzed across {(() => {
                const domainCount = new Set(
                  suggestions.suggestions.flatMap((s) =>
                    s.recommended_domains.map((d) => d.domain_name)
                  )
                ).size;
                return `${domainCount} ${domainCount === 1 ? 'domain' : 'domains'}`;
              })()}</p>
            </div>

            {/* Available Hypotheses */}
            <div className="flex flex-col gap-2 md:border-r md:border-neutral-200 md:pr-6">
              <div className="flex items-center gap-2 text-neutral-700">
                <ListChecks className="w-4 h-4 text-violet-600" />
                <p className="text-sm font-medium">Available Hypotheses</p>
              </div>
              <p className="text-4xl font-bold text-neutral-900">{totalCount}</p>
              <div className="flex items-center gap-1.5 text-xs">
                <Sparkles className="w-3 h-3 text-violet-500" />
                <span className="text-neutral-600">
                  {suggestions.ai_generated_count} AI-generated
                  {suggestions.pre_selected_count > 0 && (
                    <span className="text-neutral-500">
                      {' · '}
                      {suggestions.pre_selected_count} pre-selected
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Your Selection */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-neutral-700">
                <CheckSquare className="w-4 h-4 text-emerald-600" />
                <p className="text-sm font-medium">Your Selection</p>
              </div>
              <div className="flex items-baseline gap-2">
                <p className={`text-4xl font-bold ${selectedCount > 0 ? 'text-emerald-600' : 'text-neutral-400'}`}>
                  {selectedCount}
                </p>
                <p className="text-sm text-neutral-500">/ {totalCount}</p>
              </div>
              <p className="text-xs text-neutral-500">
                {selectedCount === 0
                  ? 'Select hypotheses below to proceed'
                  : `${Math.round((selectedCount / Math.max(totalCount, 1)) * 100)}% of available`}
              </p>
            </div>
          </div>

          {suggestions.recommendation === 'CREATE_NEW' && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                <strong>Coverage is below threshold.</strong> Consider creating custom domain
                packs or adjusting strategic questions before proceeding.
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Domain Suggestions by Strategic Question.  `space-y-8` (was 6)
          adds a bit more breathing room between questions so the visual
          grouping is clearer at a glance. */}
      <div className="space-y-8">
        {suggestions.suggestions.map((domainSuggestion, idx) => (
          // Strong left border + ring shadow + accent gradient on the
          // outer question card makes it clearly distinguishable from the
          // inner domain/hypothesis cards, which were all the same shade
          // of white-on-white before.  The thick left bar reads as "this
          // whole section is one question" no matter how far down you've
          // scrolled.
          <Card
            key={idx}
            className="border-l-4 border-l-primary-500 shadow-md ring-1 ring-primary-100 overflow-hidden"
          >
            {/* Sticky question header — pinned at viewport top while
                scrolling through this Card's hypotheses; un-sticks once
                the consultant scrolls past the Card's bottom.  Now uses
                the same primary tint as the left border so the sticky
                bar visually matches the section it belongs to (no
                ambiguity about which question is in view). */}
            <CardHeader className="sticky top-0 z-20 bg-gradient-to-r from-primary-50 to-white shadow-md border-b-2 border-primary-200">
              <div className="flex items-start gap-4">
                {/* Large question-number medallion — single dominant
                    visual anchor at the start of each section so the
                    sticky bar reads "this is Q5" at a glance. */}
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                  Q{idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs uppercase tracking-wide font-semibold text-primary-700">
                      Strategic Question
                    </span>
                    <Badge className={getCoverageColor(
                      domainSuggestion.coverage_score >= 0.8 ? 'excellent' :
                      domainSuggestion.coverage_score >= 0.6 ? 'good' :
                      domainSuggestion.coverage_score >= 0.4 ? 'moderate' : 'limited'
                    )}>
                      {Math.round(domainSuggestion.coverage_score * 100)}% Coverage
                    </Badge>
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900 leading-snug">
                    {domainSuggestion.strategic_question_text}
                  </h3>
                  <div className="flex items-center gap-4 mt-3 text-sm text-neutral-600">
                    <span>
                      Type: <strong>{domainSuggestion.question_analysis.question_type.replace('_', ' ')}</strong>
                    </span>
                    <span>
                      Driver: <strong>{domainSuggestion.question_analysis.value_driver}</strong>
                    </span>
                    <span>
                      Priority: <strong>{domainSuggestion.question_analysis.priority.toUpperCase()}</strong>
                    </span>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardBody>
              {/* Recommended Domains */}
              <div className="space-y-4">
                {domainSuggestion.recommended_domains.map((domain, domainIdx) => {
                  const domainKey = `${idx}-${domainIdx}`;
                  const isExpanded = expandedDomains.has(domainKey);
                  const hypotheses = [
                    ...domain.existing_hypotheses.map(h => ({ ...h, isAi: false })),
                    ...domain.ai_generated_hypotheses.map(h => ({ ...h, isAi: true }))
                  ];

                  return (
                    <div key={domainIdx} className="border border-neutral-200 rounded-lg">
                      <button
                        onClick={() => toggleDomain(domainKey)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-neutral-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <TrendingUp className="w-5 h-5 text-primary-600" />
                          <div className="text-left">
                            <p className="font-semibold text-neutral-900">
                              {domain.domain_name} / {domain.subdomain_name}
                            </p>
                            <p className="text-sm text-neutral-600">
                              {hypotheses.length} hypotheses • Relevance: {Math.round(domain.relevance_score * 100)}% • Quality: {Math.round(domain.quality_score * 100)}%
                            </p>
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-neutral-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-neutral-400" />
                        )}
                      </button>

                      {isExpanded && (
                        <div className="px-4 pb-4 space-y-3 border-t border-neutral-200 pt-4">
                          {hypotheses.map((hypothesis: any) => {
                            const hypId = hypothesis.hypothesis_id || hypothesis.temp_id;
                            const isSelected = selectedHypotheses.has(hypId);
                            // `idx` is the question index from the outer
                            // `suggestions.suggestions.map`.  The memo picks
                            // AT MOST one best-fit hypothesis per question
                            // (across all its domains) when there's a clear
                            // winner; nothing when the field is too tight.
                            const recommendation = recommendedHypothesisByQuestion.get(idx);
                            const isRecommended = recommendation?.id === hypId;

                            return (
                              <div
                                key={hypId}
                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  isSelected
                                    ? 'border-primary-500 bg-primary-50'
                                    : isRecommended
                                    ? 'border-emerald-300 bg-emerald-50/30 hover:border-emerald-400'
                                    : 'border-neutral-200 hover:border-neutral-300'
                                }`}
                                onClick={() => toggleHypothesis(hypId)}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 mt-1">
                                    {isSelected ? (
                                      <CheckCircle2 className="w-5 h-5 text-primary-600" />
                                    ) : (
                                      <div className="w-5 h-5 border-2 border-neutral-300 rounded-full" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                      <p className="font-semibold text-neutral-900">{hypothesis.title}</p>
                                      {isRecommended && recommendation && (
                                        <Tooltip
                                          position="top"
                                          variant="light"
                                          maxWidth={420}
                                          content={
                                            <div className="space-y-2">
                                              <p className="font-semibold text-neutral-900">
                                                Best-fit hypothesis for this question
                                                {typeof recommendation.score === 'number' && (
                                                  <span className="ml-1 font-normal text-neutral-600">
                                                    (score {recommendation.score.toFixed(2)})
                                                  </span>
                                                )}
                                              </p>
                                              {/* Per-signal breakdown from the backend ranker.
                                                  The contributions sum to the score above, so the
                                                  reader can check the arithmetic rather than take
                                                  the number on trust. */}
                                              {recommendation.signals.length > 0 ? (
                                                <ul className="space-y-1.5">
                                                  {recommendation.signals.map((s, i) => (
                                                    <li key={s.signal || i} className="text-xs">
                                                      <div className="flex items-baseline justify-between gap-3">
                                                        <span className="font-medium text-neutral-900">
                                                          {s.label}
                                                        </span>
                                                        <span className="font-mono text-neutral-600 whitespace-nowrap">
                                                          {s.value.toFixed(2)} × {s.weight.toFixed(2)} ={' '}
                                                          {s.contribution.toFixed(3)}
                                                        </span>
                                                      </div>
                                                      <p className="text-neutral-600 leading-relaxed">
                                                        {s.explanation}
                                                      </p>
                                                    </li>
                                                  ))}
                                                </ul>
                                              ) : (
                                                <p className="text-xs text-neutral-600 leading-relaxed">
                                                  Ranked highest for this question by the
                                                  recommender. No signal breakdown was returned.
                                                </p>
                                              )}
                                            </div>
                                          }
                                        >
                                          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1 cursor-help">
                                            <Sparkles className="w-3 h-3" />
                                            Recommended
                                          </Badge>
                                        </Tooltip>
                                      )}
                                      {hypothesis.isAi && (
                                        <Badge variant="secondary" className="flex items-center gap-1">
                                          <Sparkles className="w-3 h-3" />
                                          AI-Generated
                                        </Badge>
                                      )}
                                      {/* No second "Recommended" badge here.
                                          `recommendedHypothesisByQuestion` is now derived from
                                          `pre_selected` itself, so this condition and
                                          `isRecommended` are the same thing — the emerald badge
                                          above is the single indicator, and it carries the
                                          signal breakdown. */}
                                    </div>
                                    <p className="text-sm text-neutral-700 mb-3">{hypothesis.statement}</p>
                                    <p className="text-sm text-neutral-600 mb-3">
                                      <strong>Rationale:</strong> {hypothesis.rationale}
                                    </p>

                                    {/* A tick with no explanation looks arbitrary. This names the
                                        KG areas that drive Schema Design coverage later, so the
                                        recommendation is auditable rather than asserted. */}
                                    {hypothesis.pre_selected && hypothesis.recommendation_reason && (
                                      <div className="mb-3 rounded-md border-l-2 border-green-500 bg-green-50 px-3 py-2">
                                        <p className="text-xs text-green-900">
                                          <strong>Why recommended:</strong>{' '}
                                          {hypothesis.recommendation_reason}
                                        </p>
                                      </div>
                                    )}

                                    {/* KG areas this hypothesis needs. These are the same ids the
                                        schema-coverage calculation uses at Step 5, so showing them
                                        here connects the choice to what it will be measured on. */}
                                    {Array.isArray(hypothesis.kg_areas) && hypothesis.kg_areas.length > 0 && (
                                      <div className="mb-3 flex items-center gap-1.5 flex-wrap">
                                        <span className="text-xs text-neutral-500">Data areas:</span>
                                        {hypothesis.kg_areas.map((area: KGAreaType) => (
                                          <Badge
                                            key={area}
                                            variant="outline"
                                            className="text-[11px] font-normal"
                                            title={area}
                                          >
                                            {formatKGArea(area)}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}

                                    <div className="flex items-center gap-4 text-xs text-neutral-500">
                                      {!hypothesis.isAi && (
                                        <>
                                          <span>
                                            Validated: {hypothesis.validation_count} cases
                                          </span>
                                          <span>
                                            Success: {Math.round(hypothesis.success_rate * 100)}%
                                          </span>
                                        </>
                                      )}
                                      {/* AI-generated hypotheses don't carry a relevance_score
                                          per the backend Pydantic model — only existing ones
                                          do.  Guard the render so AI ones don't show
                                          "Relevance: NaN%". */}
                                      {typeof hypothesis.relevance_score === 'number' && (
                                        <span>
                                          Relevance: {Math.round(hypothesis.relevance_score * 100)}%
                                        </span>
                                      )}
                                      {hypothesis.isAi && (
                                        <span>
                                          Confidence: {Math.round(hypothesis.confidence * 100)}%
                                        </span>
                                      )}
                                    </div>

                                    {hypothesis.required_data && hypothesis.required_data.length > 0 && (
                                      <div className="mt-3 pt-3 border-t border-neutral-200">
                                        <p className="text-xs font-medium text-neutral-700 mb-2">Required Data:</p>
                                        <div className="flex flex-wrap gap-1">
                                          {hypothesis.required_data.map((data: string, i: number) => (
                                            <Badge key={i} variant="secondary" className="text-xs">
                                              {data}
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* Coverage Gaps */}
                          {domain.gaps.length > 0 && (
                            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm font-medium text-amber-900 mb-2">Coverage Gaps:</p>
                              <ul className="list-disc list-inside text-sm text-amber-800 space-y-1">
                                {domain.gaps.map((gap, gapIdx) => (
                                  <li key={gapIdx}>{gap}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={approveMutation.isPending}
        >
          Back to Discovery
        </Button>

        <div className="flex items-center gap-4">
          <p className="text-sm text-neutral-600">
            {selectedCount} of {totalCount} hypotheses selected
          </p>
          <Button
            onClick={() => approveMutation.mutate()}
            disabled={selectedCount === 0 || approveMutation.isPending}
            className="flex items-center gap-2"
          >
            {approveMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Approving...
              </>
            ) : (
              <>
                Approve Selection & Continue
                <CheckCircle2 className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Generation Metadata */}
      <div className="text-xs text-neutral-500 text-center pt-4 border-t">
        Generated by {suggestions.llm_provider} {suggestions.llm_model} •
        Processing time: {suggestions.processing_time_seconds.toFixed(1)}s •
        {new Date(suggestions.generated_at).toLocaleString()}
      </div>
    </div>
  );
};

export default Step0DomainSelection;
